//@flow
import React, {
  useRef,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from 'react';

export default function createRefContext() {
  type PartialStore = any;
  type Store = any;

  function useStoreData(
    initialState: Store
  ): {
    get: () => Store,
    set: (value: PartialStore) => void,
    subscribe: (callback: () => void) => () => void,
  } {
    const store = useRef(initialState);

    const get = useCallback(() => store.current, []);

    const subscribers = useRef(new Set<() => void>());

    const set = useCallback((value: PartialStore) => {
      store.current = {...store.current, ...value};
      subscribers.current.forEach(callback => callback());
    }, []);

    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => {
        subscribers.current.delete(callback);
      };
    }, []);

    return {
      get,
      set,
      subscribe,
    };
  }

  type UseStoreDataReturnType = $Call<typeof useStoreData>;

  const StoreContext = createContext<UseStoreDataReturnType | null>(null);

  function Provider({children, data}: {children: React$Node, data: Store}) {
    const store = useStoreData(data);

    return (
      // $FlowFixMe
      <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
    );
  }

  function useStore(
    selector: (store: Store) => any,
    shallowEqual?: (state: any, newState: any) => boolean
  ): [any, (value: PartialStore) => void] {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error('Store not found');
    }

    const [state, setState] = useState(selector(store.get()));

    useEffect(() => {
      const unsubscribe = store.subscribe(() => {
        if (shallowEqual) {
          const newState = selector(store.get());
          if (!shallowEqual(state, newState)) {
            setState(newState);
          }
          return;
        }
        setState(selector(store.get()));
      });
      return unsubscribe;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [state, store.set];
  }

  return {
    Provider,
    useStore,
  };
}
